import React, { forwardRef, ReactNode, useCallback, useEffect, useRef } from "react";

import { faLocationArrow, faLock } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import AutoComplete from "antd/es/auto-complete";
import Input, { InputRef } from "antd/es/input";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import Address from "@mapmycustomers/shared/types/Address";
import { GeocodeResult } from "@mapmycustomers/shared/types/base/Located";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import getFormattedAddressForUi from "@mapmycustomers/shared/util/formatters/getFormattedAddressForUi";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";

import poweredByGoogle from "../../../../assets/google/poweredByGoogle.png";
import { useConfigProvider } from "../../../../ConfigProvider";
import LoadingSpinner from "../../../../LoadingSpinner";
import Footer from "../../../inline/Footer";
import InlineLabeled, { InlineLabeledProps } from "../../../inline/InlineLabeled";
import { AddressPickDetails, AddressPresets } from "../../utils/AddressPresets";
import useAddressPresets from "../../utils/useAddressPresets";
import useGoogleAutoCompleteOptions, {
  MANUAL_ADDRESS_VALUE,
  NO_RESULT_VALUE,
} from "../../utils/useGoogleAutoCompleteOptions";
import usePlaceSearch from "../../utils/usePlaceSearch";

const block = bem("mmc-inline-auto-complete-address");

export interface InlineAutoCompleteAddressProps extends Omit<
  InlineLabeledProps,
  "children" | "extra"
> {
  allowClear?: boolean;
  caption?: string;
  className?: string;
  disabled?: boolean;
  disableFindMe?: boolean;
  noValueText?: ReactNode;
  onBlur?: () => void;
  onChange?: (address?: Address, details?: AddressPickDetails) => void;
  onEnterManually?: (value: string) => void;
  onPressEnter?: () => void;
  onReverseGeocodeAddress: (payload: {
    callback: (result: GeocodeResult) => void;
    coordinates: LongLat;
    failureCallback?: () => void;
  }) => void;
  placeholder?: string;
  placePredictionTypes?: string[];
  poweredByGoogleClassName?: string;
  presets?: AddressPresets;
  ref?: React.Ref<InputRef>;
  value?: Address;
}

// eslint-disable-next-line react/display-name
const AutoCompleteAddress = forwardRef<InputRef, InlineAutoCompleteAddressProps>(
  (
    {
      allowClear,
      caption,
      className,
      disabled,
      disableFindMe,
      label,
      labelClassName,
      labelPosition = "side",
      noValueText,
      onBlur,
      onChange,
      onEnterManually,
      onPressEnter,
      onReverseGeocodeAddress,
      placeholder,
      placePredictionTypes,
      poweredByGoogleClassName,
      presets,
      required,
      value,
    },
    ref
  ) => {
    const { formatMessage, useFindMyLocation } = useConfigProvider();
    const [editing, startEditing, cancelEditing] = useBoolean();
    const {
      fieldValue,
      placeList,
      placeListLoading,
      placePredictionsGetter,
      selectPlace,
      setFieldValue,
      setFieldValueWithoutSearch,
      setPlacesList,
    } = usePlaceSearch({
      isPrivateQuery: presets?.isPrivateQuery,
      onChange,
      placePredictionTypes,
      value,
    });

    // Handle clicking on any option in a dropdown
    const handleSelect = useCallback(
      (placeId: string) => {
        if ([MANUAL_ADDRESS_VALUE, NO_RESULT_VALUE].includes(placeId)) {
          if (placeId === MANUAL_ADDRESS_VALUE) {
            onEnterManually?.(fieldValue);
          }
          setFieldValue("");
          return;
        }
        const place = placeList?.find(({ place_id }) => place_id === placeId);
        if (place) {
          setFieldValue(place.description);
        }
        selectPlace(placeId);
        cancelEditing();
      },
      [cancelEditing, fieldValue, onEnterManually, placeList, selectPlace, setFieldValue]
    );

    // Find Me functionality:
    const [, , findMeLoading, findMeError, handleFindMyLocation, geocodingResult] =
      useFindMyLocation(onReverseGeocodeAddress);
    const lastGeocodingResult = useRef<GeocodeResult>();
    useEffect(() => {
      if (lastGeocodingResult.current !== geocodingResult) {
        lastGeocodingResult.current = geocodingResult;
        // the resolved address is not a search result, so drop back to the un-searched state
        setPlacesList(undefined);
        onChange?.(geocodingResult?.address);
      }
    }, [geocodingResult, onChange, setPlacesList]);

    const options = useGoogleAutoCompleteOptions(placeList, !!onEnterManually);

    const checkTextAndFindPlaceIfNeeded = useCallback(async () => {
      if (getFormattedAddressForUi(value) !== fieldValue) {
        const places = await placePredictionsGetter(fieldValue);
        if (places?.length) {
          selectPlace(places[0].place_id);
        } else if (!places) {
          setFieldValueWithoutSearch(getFormattedAddressForUi(value));
        } else if (allowClear) {
          onChange?.(undefined);
        }
      }
      cancelEditing();
    }, [
      value,
      fieldValue,
      placePredictionsGetter,
      allowClear,
      cancelEditing,
      selectPlace,
      setFieldValueWithoutSearch,
      onChange,
    ]);

    const handleClear = useCallback(() => {
      onChange?.(undefined);
      setFieldValueWithoutSearch("");
    }, [onChange, setFieldValueWithoutSearch]);

    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLElement>) => {
        if (event.key === "Escape") {
          setFieldValueWithoutSearch(getFormattedAddressForUi(value));
          // Ending the edit unmounts the input, which doesn't reliably fire blur
          onBlur?.();
          cancelEditing();
        } else if (event.key === "Enter") {
          checkTextAndFindPlaceIfNeeded();
        }
      },
      [cancelEditing, checkTextAndFindPlaceIfNeeded, onBlur, setFieldValueWithoutSearch, value]
    );

    const presetProps = useAddressPresets({
      fieldValue,
      onBlur,
      onChange,
      onEditEnd: cancelEditing,
      onKeyDown: handleKeyDown,
      onSelect: handleSelect,
      onTextBlur: checkTextAndFindPlaceIfNeeded,
      options,
      presets,
      setFieldValue,
      setFieldValueWithoutSearch,
      setPlacesList,
      value,
    });

    const inputRef = useRef<HTMLInputElement>(null);
    const handleStartEditing = useCallback(() => {
      if (disabled) {
        return;
      }
      startEditing();
      // can't focus hidden field, need to wait until it will be displayed
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }, [disabled, startEditing]);

    return (
      <div className={cn(block({ disabled }), className)}>
        <InlineLabeled
          extra={disabled ? <FontAwesomeIcon className={block("lock")} icon={faLock} /> : undefined}
          label={
            <div className={block("label-container")}>
              {label}
              <img
                alt="powered by Google"
                className={poweredByGoogleClassName}
                src={poweredByGoogle}
              />
            </div>
          }
          labelClassName={cn(block("label"), labelClassName)}
          labelPosition={labelPosition}
          required={required}
        >
          {editing && !disabled ? (
            <AutoComplete
              autoFocus
              className={cn(block("input-container"), className)}
              disabled={disabled}
              dropdownMatchSelectWidth={false}
              onBlur={disabled ? undefined : presetProps.onBlur}
              onChange={disabled ? undefined : presetProps.onChange}
              onClear={disabled ? undefined : handleClear}
              onDropdownVisibleChange={presetProps.onDropdownVisibleChange}
              onFocus={disabled ? undefined : presetProps.onFocus}
              onKeyDown={disabled ? undefined : presetProps.onKeyDown}
              onSelect={disabled ? undefined : presetProps.onSelect}
              open={presetProps.open}
              options={presetProps.options}
              showAction={presetProps.showAction}
              showSearch={!disabled}
              value={fieldValue}
            >
              <Input
                allowClear={allowClear && !disabled}
                className={block("input")}
                disabled={disabled}
                onPressEnter={onPressEnter}
                placeholder={
                  disabled
                    ? undefined
                    : (placeholder ?? formatMessage("ui.address.autoCompleteAddress.placeholder"))
                }
                ref={ref}
                size="large"
                suffix={
                  findMeLoading || placeListLoading ? (
                    <LoadingSpinner mini />
                  ) : disableFindMe || disabled ? undefined : (
                    <Tooltip title={formatMessage("ui.address.autoCompleteAddress.tooltip")}>
                      <FontAwesomeIcon
                        className={block("icon")}
                        icon={faLocationArrow}
                        onClick={handleFindMyLocation}
                        // PreventDefault so clicking the icon does not blur the address input —
                        // onBlur cancels editing before onClick can fire
                        onMouseDown={(e) => e.preventDefault()}
                        size="lg"
                      />
                    </Tooltip>
                  )
                }
              />
            </AutoComplete>
          ) : (
            <div className={cn(block("value"))} onClick={handleStartEditing}>
              {value ? (
                getFormattedAddressForUi(value)
              ) : (
                <span className={block("unknown")}>
                  {noValueText ?? formatMessage("ui.inlineInput.unknown")}
                </span>
              )}
            </div>
          )}
        </InlineLabeled>
        <Footer caption={findMeError ?? caption} disabled={true} editing={false} />
      </div>
    );
  }
);

export default AutoCompleteAddress;
